import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const scanningMediaScanConfigurationValueSchema = z.strictObject({
    projectPanelEnabled: z.boolean(),
    maximumCapturesPerProject: z.number().int().min(1).max(500),
    maximumFloorsPerCapture: z.number().int().min(1).max(25),
    maximumRoomsPerCapture: z.number().int().min(1).max(250),
});
export const scanningMediaScanConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.scans.configuration',
        moduleId: 'scanning-media',
        title: 'Scanning and Media capture configuration',
        helpText: 'Controls tenant capture limits and whether the Project-hosted semantic readback is available.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            projectPanelEnabled: true,
            maximumCapturesPerProject: 50,
            maximumFloorsPerCapture: 10,
            maximumRoomsPerCapture: 100,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: scanningMediaScanConfigurationValueSchema,
});
export const scanningMediaScanDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showDeviceMetadata: z.boolean(),
    showQualityEvents: z.boolean(),
    showUnreviewedCaptures: z.boolean(),
    measurementSystem: z.enum(['metric', 'imperial']),
});
export const scanningMediaScanDisplaySetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.scans.display',
        moduleId: 'scanning-media',
        title: 'Scanning and Media display',
        helpText: 'Controls card size, density, visible evidence, and measurement presentation.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            density: 'comfortable',
            cardSize: 'standard',
            showDeviceMetadata: true,
            showQualityEvents: true,
            showUnreviewedCaptures: true,
            measurementSystem: 'imperial',
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: scanningMediaScanDisplayValueSchema,
});
export const scanningMediaScanCopyValueSchema = z.strictObject({
    panelTitle: z.string().trim().min(1).max(120),
    panelDescription: z.string().trim().min(1).max(300),
    loadingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    reviewedLabel: z.string().trim().min(1).max(80),
    draftLabel: z.string().trim().min(1).max(80),
    archivedLabel: z.string().trim().min(1).max(80),
    roomsLabel: z.string().trim().min(1).max(80),
    floorsLabel: z.string().trim().min(1).max(80),
    floorAreaLabel: z.string().trim().min(1).max(80),
    wallAreaLabel: z.string().trim().min(1).max(80),
    baseboardLabel: z.string().trim().min(1).max(80),
    openingsLabel: z.string().trim().min(1).max(80),
    qualityLabel: z.string().trim().min(1).max(80),
    deviceLabel: z.string().trim().min(1).max(80),
    acceptedVersionLabel: z.string().trim().min(1).max(100),
    latestVersionLabel: z.string().trim().min(1).max(100),
    rawEvidenceNotice: z.string().trim().min(1).max(300),
});
export const scanningMediaScanCopySetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.copy.scans',
        moduleId: 'scanning-media',
        title: 'Scanning and Media copy',
        helpText: 'Controls tenant-editable scan evidence, measurement, and lifecycle labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelTitle: 'Room scans and measurements',
            panelDescription: 'Review retained semantic room evidence and estimator-ready measurements for this Project.',
            loadingLabel: 'Loading room scans…',
            loadError: 'Room scans could not be loaded.',
            emptyTitle: 'No room scans yet',
            emptyDescription: 'A compatible CREST Field capture can add semantic room evidence without changing ordinary Project files.',
            reviewedLabel: 'Reviewed',
            draftLabel: 'Needs review',
            archivedLabel: 'Archived',
            roomsLabel: 'Rooms',
            floorsLabel: 'Floors',
            floorAreaLabel: 'Floor area',
            wallAreaLabel: 'Net wall area',
            baseboardLabel: 'Baseboard',
            openingsLabel: 'Doors and windows',
            qualityLabel: 'Quality evidence',
            deviceLabel: 'Capture device',
            acceptedVersionLabel: 'Accepted version',
            latestVersionLabel: 'Latest version',
            rawEvidenceNotice: 'Corrections create a new immutable semantic version. Raw captured evidence remains unchanged.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: scanningMediaScanCopyValueSchema,
});
export const scanningMediaAssemblyConfigurationValueSchema = z.strictObject({
    projectAssemblyPanelEnabled: z.boolean(),
    touchToleranceMm: z.number().int().min(0).max(5_000),
    minimumSharedBoundaryMm: z.number().int().min(0).max(50_000),
    requireSingleConnectedComponent: z.boolean(),
    openingRematchToleranceMm: z.number().int().min(0).max(5_000),
    maximumAssemblyRevisionsPerCapture: z.number().int().min(1).max(1_000),
});
export const scanningMediaAssemblyConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.assembly.configuration',
        moduleId: 'scanning-media',
        title: 'Scanning and Media assembly configuration',
        helpText: 'Controls protected room-touch validation, conservative opening rematching, and retained revision limits.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            projectAssemblyPanelEnabled: true,
            touchToleranceMm: 229,
            minimumSharedBoundaryMm: 457,
            requireSingleConnectedComponent: false,
            openingRematchToleranceMm: 300,
            maximumAssemblyRevisionsPerCapture: 100,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: scanningMediaAssemblyConfigurationValueSchema,
});
export const scanningMediaAssemblyDisplayValueSchema = z.strictObject({
    panelLayout: z.enum(['stacked', 'split']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showValidationDetails: z.boolean(),
    showTouchGraph: z.boolean(),
    placementStepMm: z.number().int().min(1).max(1_000),
});
export const scanningMediaAssemblyDisplaySetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.assembly.display',
        moduleId: 'scanning-media',
        title: 'Scanning and Media assembly display',
        helpText: 'Controls assembly review layout, card size, visible validation detail, and placement step.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelLayout: 'split',
            cardSize: 'standard',
            showValidationDetails: true,
            showTouchGraph: true,
            placementStepMm: 25,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: scanningMediaAssemblyDisplayValueSchema,
});
export const scanningMediaAssemblyCopyValueSchema = z.strictObject({
    panelTitle: z.string().trim().min(1).max(120),
    panelDescription: z.string().trim().min(1).max(300),
    loadingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    noCaptureTitle: z.string().trim().min(1).max(120),
    noCaptureDescription: z.string().trim().min(1).max(300),
    noDraftTitle: z.string().trim().min(1).max(120),
    noDraftDescription: z.string().trim().min(1).max(300),
    latestRevisionLabel: z.string().trim().min(1).max(100),
    acceptedRevisionLabel: z.string().trim().min(1).max(100),
    validationPassedLabel: z.string().trim().min(1).max(120),
    validationFailedLabel: z.string().trim().min(1).max(160),
    disconnectedWarning: z.string().trim().min(1).max(240),
    saveDraftAction: z.string().trim().min(1).max(80),
    acceptRevisionAction: z.string().trim().min(1).max(80),
    boundaryTitle: z.string().trim().min(1).max(120),
    boundaryDescription: z.string().trim().min(1).max(300),
    correctionReasonLabel: z.string().trim().min(1).max(120),
    saveBoundaryAction: z.string().trim().min(1).max(80),
    missingWallLabel: z.string().trim().min(1).max(120),
    captureLabel: z.string().trim().min(1).max(80),
    roomLabel: z.string().trim().min(1).max(80),
    placementsTitle: z.string().trim().min(1).max(120),
    xOffsetLabel: z.string().trim().min(1).max(80),
    zOffsetLabel: z.string().trim().min(1).max(80),
    rotationLabel: z.string().trim().min(1).max(80),
    boundaryVerticesLabel: z.string().trim().min(1).max(160),
    missingWallEdgesLabel: z.string().trim().min(1).max(160),
    openingRematchesLabel: z.string().trim().min(1).max(180),
    emptyOptionLabel: z.string().trim().min(1).max(80),
    staleAuthorityLabel: z.string().trim().min(1).max(160),
    defaultAssemblyReason: z.string().trim().min(10).max(240),
    defaultBoundaryReason: z.string().trim().min(10).max(240),
    rawEvidenceNotice: z.string().trim().min(1).max(300),
});
export const scanningMediaAssemblyCopySetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.copy.assembly',
        moduleId: 'scanning-media',
        title: 'Scanning and Media assembly copy',
        helpText: 'Controls tenant-editable assembly review, validation, and acceptance labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelTitle: 'Room assembly review',
            panelDescription: 'Review exact room placement revisions without changing retained scan geometry.',
            loadingLabel: 'Loading room assembly…',
            loadError: 'Room assembly could not be loaded.',
            noCaptureTitle: 'No reviewed scan selected',
            noCaptureDescription: 'Accept a semantic scan version before accepting a room assembly.',
            noDraftTitle: 'No assembly draft yet',
            noDraftDescription: 'Create a deterministic or manual placement revision for review.',
            latestRevisionLabel: 'Latest revision',
            acceptedRevisionLabel: 'Accepted revision',
            validationPassedLabel: 'Connected/touching validation passed',
            validationFailedLabel: 'Assembly needs correction before acceptance',
            disconnectedWarning: 'Separate room components are retained and shown until evidence connects them.',
            saveDraftAction: 'Save assembly draft',
            acceptRevisionAction: 'Accept reviewed revision',
            boundaryTitle: 'Boundary and missing-wall correction',
            boundaryDescription: 'Adjust exact room vertices or mark a full pass-through. Openings must rematch unambiguously.',
            correctionReasonLabel: 'Reviewed change reason',
            saveBoundaryAction: 'Save corrected semantic version',
            missingWallLabel: 'Opening — no wall here',
            captureLabel: 'Scan capture',
            roomLabel: 'Room',
            placementsTitle: 'Room placements',
            xOffsetLabel: 'X offset (mm)',
            zOffsetLabel: 'Z offset (mm)',
            rotationLabel: 'Rotation (degrees)',
            boundaryVerticesLabel: 'Boundary vertices (x,y,z millimeters; one per line)',
            missingWallEdgesLabel: 'Missing-wall edge indexes (zero-based, comma separated)',
            openingRematchesLabel: 'Opening rematches (opening key:edge index; one per line)',
            emptyOptionLabel: 'None selected',
            staleAuthorityLabel: 'Stale after semantic correction',
            defaultAssemblyReason: 'Record the reviewed provider-neutral room assembly revision',
            defaultBoundaryReason: 'Correct the reviewed room boundary while retaining raw scan evidence',
            rawEvidenceNotice: 'Assembly and boundary corrections create immutable revisions. Raw scan evidence is never overwritten.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: scanningMediaAssemblyCopyValueSchema,
});
export const scanningMediaAssetConfigurationValueSchema = z.strictObject({
    projectAssetPanelEnabled: z.boolean(),
    maximumAssetsPerCapture: z.number().int().min(1).max(10_000),
    maximumAssetSizeBytes: z.number().int().min(1).max(5 * 1_024 * 1_024 * 1_024),
    allowedAssetKinds: z.array(z.enum([
        'raw-roomplan', 'usdz-model', 'detail-mesh', 'posed-photo', 'panorama', 'depth-map',
        'floor-plan-preview', 'measurement-report', 'scan-package', 'thumbnail', 'other',
    ])).min(1).max(11),
});
export const scanningMediaAssetConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.assets.configuration',
        moduleId: 'scanning-media',
        title: 'Scanning and Media asset configuration',
        helpText: 'Controls protected private-asset count, byte-size, type, and Project-panel limits.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            projectAssetPanelEnabled: true,
            maximumAssetsPerCapture: 1_000,
            maximumAssetSizeBytes: 2 * 1_024 * 1_024 * 1_024,
            allowedAssetKinds: [
                'raw-roomplan', 'usdz-model', 'detail-mesh', 'posed-photo', 'panorama', 'depth-map',
                'floor-plan-preview', 'measurement-report', 'scan-package', 'thumbnail', 'other',
            ],
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: scanningMediaAssetConfigurationValueSchema,
});
export const scanningMediaAssetDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showAnchors: z.boolean(),
    showSourceMetadata: z.boolean(),
    showHashes: z.boolean(),
    showQuarantined: z.boolean(),
    showArchived: z.boolean(),
});
export const scanningMediaAssetDisplaySetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.assets.display',
        moduleId: 'scanning-media',
        title: 'Scanning and Media asset display',
        helpText: 'Controls private asset card density, size, technical evidence, and retained states.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            density: 'comfortable',
            cardSize: 'standard',
            showAnchors: true,
            showSourceMetadata: true,
            showHashes: false,
            showQuarantined: true,
            showArchived: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: scanningMediaAssetDisplayValueSchema,
});
export const scanningMediaAssetCopyValueSchema = z.strictObject({
    panelTitle: z.string().trim().min(1).max(120),
    panelDescription: z.string().trim().min(1).max(300),
    loadingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    captureLabel: z.string().trim().min(1).max(80),
    kindLabel: z.string().trim().min(1).max(80),
    kindLabels: z.strictObject({
        'raw-roomplan': z.string().trim().min(1).max(80),
        'usdz-model': z.string().trim().min(1).max(80),
        'detail-mesh': z.string().trim().min(1).max(80),
        'posed-photo': z.string().trim().min(1).max(80),
        panorama: z.string().trim().min(1).max(80),
        'depth-map': z.string().trim().min(1).max(80),
        'floor-plan-preview': z.string().trim().min(1).max(80),
        'measurement-report': z.string().trim().min(1).max(80),
        'scan-package': z.string().trim().min(1).max(80),
        thumbnail: z.string().trim().min(1).max(80),
        other: z.string().trim().min(1).max(80),
    }),
    stateLabel: z.string().trim().min(1).max(80),
    fileNameLabel: z.string().trim().min(1).max(80),
    contentTypeLabel: z.string().trim().min(1).max(80),
    sizeLabel: z.string().trim().min(1).max(80),
    hashLabel: z.string().trim().min(1).max(80),
    anchorLabel: z.string().trim().min(1).max(80),
    emptyAnchorLabel: z.string().trim().min(1).max(80),
    capturedAtLabel: z.string().trim().min(1).max(80),
    sequenceLabel: z.string().trim().min(1).max(80),
    confidenceLabel: z.string().trim().min(1).max(80),
    storageVerifiedAtLabel: z.string().trim().min(1).max(80),
    storageAttachedAtLabel: z.string().trim().min(1).max(80),
    registeredLabel: z.string().trim().min(1).max(80),
    activeLabel: z.string().trim().min(1).max(80),
    quarantinedLabel: z.string().trim().min(1).max(80),
    archivedLabel: z.string().trim().min(1).max(80),
    privateEvidenceNotice: z.string().trim().min(1).max(300),
});
export const scanningMediaAssetCopySetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.copy.assets',
        moduleId: 'scanning-media',
        title: 'Scanning and Media asset copy',
        helpText: 'Controls tenant-editable private scan-asset evidence and lifecycle labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelTitle: 'Private scan evidence',
            panelDescription: 'Review retained scan asset metadata without exposing private object keys.',
            loadingLabel: 'Loading private scan evidence…',
            loadError: 'Private scan evidence could not be loaded.',
            emptyTitle: 'No private scan assets',
            emptyDescription: 'Compatible capture clients can register retained raw, model, photo, and report evidence.',
            captureLabel: 'Scan capture',
            kindLabel: 'Asset type',
            kindLabels: {
                'raw-roomplan': 'Raw RoomPlan evidence',
                'usdz-model': 'USDZ model',
                'detail-mesh': 'Detail mesh',
                'posed-photo': 'Posed photo',
                panorama: 'Panorama',
                'depth-map': 'Depth map',
                'floor-plan-preview': 'Floor-plan preview',
                'measurement-report': 'Measurement report',
                'scan-package': 'Scan package',
                thumbnail: 'Thumbnail',
                other: 'Other scan evidence',
            },
            stateLabel: 'State',
            fileNameLabel: 'File',
            contentTypeLabel: 'Content type',
            sizeLabel: 'Size',
            hashLabel: 'SHA-256',
            anchorLabel: 'Anchor',
            emptyAnchorLabel: 'Capture-wide',
            capturedAtLabel: 'Captured',
            sequenceLabel: 'Capture sequence',
            confidenceLabel: 'Anchor confidence',
            storageVerifiedAtLabel: 'Storage verified',
            storageAttachedAtLabel: 'Storage attached',
            registeredLabel: 'Registered',
            activeLabel: 'Verified and attached',
            quarantinedLabel: 'Quarantined',
            archivedLabel: 'Archived',
            privateEvidenceNotice: 'Private object keys and access URLs remain Platform-owned and are never included in this view.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: scanningMediaAssetCopyValueSchema,
});
export const scanningMediaCoordinateConfigurationValueSchema = z.strictObject({
    projectCoordinatePanelEnabled: z.boolean(),
    maximumCandidatesPerCapture: z.number().int().min(1).max(1_000),
    maximumSourcesPerCandidate: z.number().int().min(2).max(50),
});
export const scanningMediaCoordinateConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.coordinate-continuity.configuration',
        moduleId: 'scanning-media',
        title: 'Scanning and Media coordinate-continuity configuration',
        helpText: 'Controls protected retained stitch-candidate limits and the Project review surface.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            projectCoordinatePanelEnabled: true,
            maximumCandidatesPerCapture: 100,
            maximumSourcesPerCandidate: 20,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: scanningMediaCoordinateConfigurationValueSchema,
});
export const scanningMediaCoordinateDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showSourceEvidence: z.boolean(),
    showRelationships: z.boolean(),
    showRejectedCandidates: z.boolean(),
});
export const scanningMediaCoordinateDisplaySetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.coordinate-continuity.display',
        moduleId: 'scanning-media',
        title: 'Scanning and Media coordinate-continuity display',
        helpText: 'Controls coordinate evidence density, card size, relationships, and rejected history.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            density: 'comfortable',
            cardSize: 'standard',
            showSourceEvidence: true,
            showRelationships: true,
            showRejectedCandidates: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: scanningMediaCoordinateDisplayValueSchema,
});
export const scanningMediaCoordinateCopyValueSchema = z.strictObject({
    panelTitle: z.string().trim().min(1).max(120),
    panelDescription: z.string().trim().min(1).max(300),
    loadingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    candidateLabel: z.string().trim().min(1).max(80),
    acceptedLabel: z.string().trim().min(1).max(80),
    rejectedLabel: z.string().trim().min(1).max(80),
    sourcesLabel: z.string().trim().min(1).max(80),
    relationshipsLabel: z.string().trim().min(1).max(80),
    sourceUnitsLabel: z.string().trim().min(1).max(80),
    elevationLabel: z.string().trim().min(1).max(80),
    fingerprintLabel: z.string().trim().min(1).max(80),
    validationPassedLabel: z.string().trim().min(1).max(140),
    validationFailedLabel: z.string().trim().min(1).max(180),
    decisionReasonLabel: z.string().trim().min(1).max(100),
    acceptAction: z.string().trim().min(1).max(80),
    rejectAction: z.string().trim().min(1).max(80),
    defaultAcceptReason: z.string().trim().min(10).max(240),
    defaultRejectReason: z.string().trim().min(10).max(240),
    evidenceNotice: z.string().trim().min(1).max(300),
});
export const scanningMediaCoordinateCopySetting = definePlatformSetting({
    definition: {
        key: 'scanning-media.copy.coordinate-continuity',
        moduleId: 'scanning-media',
        title: 'Scanning and Media coordinate-continuity copy',
        helpText: 'Controls tenant-editable coordinate provenance, validation, and decision labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelTitle: 'Coordinate continuity',
            panelDescription: 'Review multi-capture visit-frame candidates before they become measurement authority.',
            loadingLabel: 'Loading coordinate evidence…',
            loadError: 'Coordinate-continuity evidence could not be loaded.',
            emptyTitle: 'No coordinate candidates',
            emptyDescription: 'Compatible capture clients can submit explicit multi-room or multi-floor stitch evidence.',
            candidateLabel: 'Candidate',
            acceptedLabel: 'Accepted',
            rejectedLabel: 'Rejected',
            sourcesLabel: 'Source components',
            relationshipsLabel: 'Stitch relationships',
            sourceUnitsLabel: 'Source units',
            elevationLabel: 'Visit elevation',
            fingerprintLabel: 'Source-set fingerprint',
            validationPassedLabel: 'All source and relationship evidence is eligible for operator acceptance',
            validationFailedLabel: 'Retained as evidence only — one or more authority checks failed',
            decisionReasonLabel: 'Decision reason',
            acceptAction: 'Accept coordinate candidate',
            rejectAction: 'Reject coordinate candidate',
            defaultAcceptReason: 'Accept the reviewed coordinate-continuity candidate as semantic authority',
            defaultRejectReason: 'Reject the coordinate-continuity candidate while retaining its evidence',
            evidenceNotice: 'Partial, mixed, ambiguous, or untrusted components remain immutable evidence and never become authority automatically.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: scanningMediaCoordinateCopyValueSchema,
});
export const scanningMediaSettingRegistrations = [
    scanningMediaScanConfigurationSetting,
    scanningMediaScanDisplaySetting,
    scanningMediaScanCopySetting,
    scanningMediaAssemblyConfigurationSetting,
    scanningMediaAssemblyDisplaySetting,
    scanningMediaAssemblyCopySetting,
    scanningMediaAssetConfigurationSetting,
    scanningMediaAssetDisplaySetting,
    scanningMediaAssetCopySetting,
    scanningMediaCoordinateConfigurationSetting,
    scanningMediaCoordinateDisplaySetting,
    scanningMediaCoordinateCopySetting,
];
//# sourceMappingURL=scanning-media-setting-definitions.js.map